import React from 'react';
import { View, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Text, Button } from '@app/design-system';
import { useThemeColors, useThemeAccent } from '@app/core';
import { useWebsiteBuilder } from '../../store/websiteBuilderStore';

interface TopToolbarProps {
  onBack?: () => void;
  onHome?: () => void;
  onManageShop?: () => void;
  onManageCatalog?: () => void;
  onManageUser?: () => void;
  onManageOrders?: () => void;
  onWebsiteBuilder?: () => void;
  onLogout?: () => void;
}

export const TopToolbar: React.FC<TopToolbarProps> = ({
  onBack,
  onHome,
  onManageShop,
  onManageCatalog,
  onManageUser,
  onManageOrders,
  onWebsiteBuilder,
  onLogout
}) => {
  const themeColors = useThemeColors() || { primary: '#34C759', secondary: '#F5F5F5', text: '#000000', background: '#FFFFFF' };
  const accentColor = useThemeAccent() || '#FF9500';
  const {
    viewport,
    zoom,
    isDirty,
    isSaving,
    isPublishing,
    lastSaved,
    isNewConfig,
    history,
    historyIndex,
    setViewport,
    setZoom,
    undo,
    redo,
    save,
    saveTemplate,
    publish,
    isPreview,
    isTemplateMode,
    selectedShopType,
  } = useWebsiteBuilder();


  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const handleSave = () => {
    if (isTemplateMode && selectedShopType) {
      saveTemplate(selectedShopType);
    } else {
      save();
    }
  };

  const styles = StyleSheet.create({
    container: {
      height: 60,
      backgroundColor: themeColors.primary,
      paddingVertical: 12,
      borderBottomColor: themeColors.border || '#E5E5E5',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      ...Platform.select({
        web: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }
      })
    },
    leftSection: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    centerSection: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    rightSection: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    title: {
      fontSize: 18,
      fontWeight: '700',
      color: themeColors.text || "#000000",
    },
    iconButton: {
      width: 36,
      height: 36,
      borderRadius: 6,
      backgroundColor: themeColors.secondary,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: themeColors.border || "#E5E5E5",
    },
    iconButtonDisabled: {
      opacity: 0.4,
    },
    viewportButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: themeColors.border || "#E5E5E5",
      backgroundColor: themeColors.secondary,
    },
    viewportButtonActive: {
      backgroundColor: accentColor,
      borderColor: accentColor,
    },
    viewportText: {
      fontSize: 14,
      color: themeColors.text || "#000000",
    },
    viewportTextActive: {
      color: '#FFFFFF',
      fontWeight: '600',
    },
    zoomButton: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 6,
      backgroundColor: themeColors.secondary,
      borderWidth: 1,
      borderColor: themeColors.border || "#E5E5E5",
      minWidth: 70,
      alignItems: 'center',
    },
    zoomText: {
      fontSize: 14,
      color: themeColors.text || "#000000",
    },
    statusText: {
      fontSize: 12,
      color: themeColors.text || "#666666",
    },
    saveButton: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 6,
      backgroundColor: isDirty ? accentColor : themeColors.secondary,
      borderWidth: 1,
      borderColor: isDirty ? accentColor : themeColors.border || "#E5E5E5",
    },
    saveButtonText: {
      fontSize: 14,
      color: isDirty ? '#FFFFFF' : themeColors.text || "#000000",
      fontWeight: '600',
    },
    publishButton: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 6,
      backgroundColor: accentColor || '#34C759',
    },
    publishButtonText: {
      fontSize: 14,
      color: '#FFFFFF',
      fontWeight: '600',
    },
  });

  const viewportOptions = [
    { value: 'desktop', icon: '🖥️', label: 'Desktop' },
    { value: 'tablet', icon: '📱', label: 'Tablet' },
    { value: 'mobile', icon: '📱', label: 'Mobile' },
  ] as const;

  const zoomLevels = [50, 75, 100, 125, 150];

  return (
    <View style={styles.container}>
      {/* Left Section */}
      <View style={styles.leftSection}>
        {/* Shop Home Button */}
        {onHome && (
          <TouchableOpacity
            style={styles.iconButton}
            onPress={onHome}
          >
            <Text style={{ fontSize: 18 }}>🏠</Text>
          </TouchableOpacity>
        )}

        <View>
          <Text style={styles.title}>
            {isTemplateMode ? '👑 Template Manager' : 'Website Builder'}
          </Text>
          {isTemplateMode && selectedShopType && (
            <Text style={{ fontSize: 12, color: accentColor, fontWeight: '600' }}>
              {selectedShopType}
            </Text>
          )}
        </View>

        {/* Undo/Redo */}
        <TouchableOpacity
          style={[styles.iconButton, !canUndo && styles.iconButtonDisabled]}
          onPress={undo}
          disabled={!canUndo}
        >
          <Text style={{ fontSize: 18 }}>↶</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.iconButton, !canRedo && styles.iconButtonDisabled]}
          onPress={redo}
          disabled={!canRedo}
        >
          <Text style={{ fontSize: 18 }}>↷</Text>
        </TouchableOpacity>
      </View>

      {/* Center Section - Viewport & Zoom */}
      <View style={styles.centerSection}>
        {/* Viewport Switcher */}
        {viewportOptions.map((option) => (
          <TouchableOpacity
            key={option.value}
            style={[
              styles.viewportButton,
              viewport === option.value && styles.viewportButtonActive,
            ]}
            onPress={() => setViewport(option.value)}
          >
            <Text
              style={[
                styles.viewportText,
                viewport === option.value && styles.viewportTextActive,
              ]}
            >
              {option.icon}
            </Text>
          </TouchableOpacity>
        ))}

        {/* Zoom Control */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <TouchableOpacity
            style={styles.iconButton}
            onPress={() => {
              const currentIndex = zoomLevels.indexOf(zoom);
              if (currentIndex > 0) {
                setZoom(zoomLevels[currentIndex - 1]);
              }
            }}
            disabled={zoom <= zoomLevels[0]}
          >
            <Text>−</Text>
          </TouchableOpacity>

          <View style={styles.zoomButton}>
            <Text style={styles.zoomText}>{zoom}%</Text>
          </View>

          <TouchableOpacity
            style={styles.iconButton}
            onPress={() => {
              const currentIndex = zoomLevels.indexOf(zoom);
              if (currentIndex < zoomLevels.length - 1) {
                setZoom(zoomLevels[currentIndex + 1]);
              }
            }}
            disabled={zoom >= zoomLevels[zoomLevels.length - 1]}
          >
            <Text>+</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Right Section - Actions */}
      <View style={styles.rightSection}>
        {/* Status */}
        {lastSaved && (
          <Text style={styles.statusText}>
            {isDirty ? 'Unsaved changes' : `Saved ${formatTimeSince(lastSaved)}`}
          </Text>
        )}

        {/* Save Button */}
        <TouchableOpacity
          style={styles.saveButton}
          onPress={handleSave}
          disabled={isSaving || !isDirty}
        >
          <Text style={styles.saveButtonText}>
            {isSaving
              ? 'Saving...'
              : isTemplateMode
                ? 'Save Template'
                : isNewConfig
                  ? 'Save'
                  : 'Update'
            }
          </Text>
        </TouchableOpacity>

        {/* Publish Button - Only for non-template mode */}
        {!isTemplateMode && (
          <TouchableOpacity
            style={styles.publishButton}
            onPress={publish}
            disabled={isPublishing || isDirty}
          >
            <Text style={styles.publishButtonText}>
              {isPublishing ? 'Publishing...' : 'Publish'}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

function formatTimeSince(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

